import { ConfirmationAction } from '@app/components/ConfirmationAction';
import { LDateTime } from '@app/components/date/LDateTime';
import { useToast } from '@app/core/notifications';
import {
  socialExportPublishCardMessages,
  socialExportPublishMessages,
} from '@app/modules/social-export/publish';
import { socialExport } from '@mi-front/api-clients';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import SendIcon from '@mui/icons-material/Send';
import { Alert, Box, Button, Stack } from '@mui/material';
import AlertTitle from '@mui/material/AlertTitle';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';

export function SocialUploadScheduledStatus({
  upload,
  scheduledData,
}: {
  upload: socialExport.UploadDto;
  scheduledData: socialExport.UploadScheduledDataDto;
}) {
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const queryClient = useQueryClient();

  const deleteMutation = socialExport.useDeleteUpload({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: socialExport.getUploadByKeyQueryKey(upload.key, upload.platform),
        });
        notifySuccess($t(socialExportPublishMessages.publicationDeleted));
      },
      onError: (error: Error) => {
        notifyError(error?.message || $t(socialExportPublishMessages.errorDeletingPublication));
      },
    },
  });

  const promoteMutation = socialExport.usePromoteUpload({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: socialExport.getUploadByKeyQueryKey(upload.key, upload.platform),
        });
        notifySuccess($t(socialExportPublishMessages.publicationStarted));
      },
      onError: (error: Error) => {
        notifyError(error?.message || $t(socialExportPublishMessages.errorPublishingNow));
      },
    },
  });

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ id: upload.id });
  };

  const handlePromote = async () => {
    await promoteMutation.mutateAsync({ id: upload.id });
  };

  return (
    <Alert severity="info" icon={<ScheduleSendIcon />}>
      <AlertTitle>
        <FormattedMessage {...socialExportPublishMessages.scheduledAlertTitle} />
      </AlertTitle>
      <Box mt={1}>
        <FormattedMessage {...socialExportPublishMessages.videoWillPublishAt} />{' '}
        <strong>
          <LDateTime value={scheduledData.publishAt} showRelative="inline" />
        </strong>
      </Box>
      <Box mt={2}>
        <Stack direction="row" spacing={2}>
          <ConfirmationAction
            title={$t(socialExportPublishCardMessages.publishNowConfirmTitle)}
            message={
              <FormattedMessage {...socialExportPublishCardMessages.publishNowConfirmMessage} />
            }
            confirmLabel={$t(socialExportPublishCardMessages.publishNow)}
            cancelLabel={$t({
              id: 'common.cancel',
              defaultMessage: 'Cancelar',
            })}
            confirmColor="primary"
            onConfirm={handlePromote}
          >
            {({ onClick }) => (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={onClick}
              >
                <FormattedMessage {...socialExportPublishCardMessages.publishNow} />
              </Button>
            )}
          </ConfirmationAction>
          <ConfirmationAction
            title={$t(socialExportPublishCardMessages.deleteScheduledPublishTitle)}
            message={
              <FormattedMessage
                {...socialExportPublishCardMessages.deleteScheduledPublishMessage}
              />
            }
            confirmLabel={$t({
              id: 'common.delete',
              defaultMessage: 'Eliminar',
            })}
            cancelLabel={$t({
              id: 'common.cancel',
              defaultMessage: 'Cancelar',
            })}
            confirmColor="error"
            onConfirm={handleDelete}
          >
            {({ onClick }) => (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={onClick}
              >
                <FormattedMessage id="common.delete" defaultMessage="Eliminar" />
              </Button>
            )}
          </ConfirmationAction>
        </Stack>
      </Box>
    </Alert>
  );
}
